import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import {
    Avatar,
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import { matchColor } from '@components/Common/MatchPercent';
import React from 'react';
import './RelevancyCandidatesTable.scss';

interface Candidate {
    jobSeekerId: string;
    firstName: string;
    lastName: string;
    currentRole?: string;
    score?: number;
    recommendation?: 'SHORTLIST' | 'MAYBE' | 'REJECT' | 'HARD_REJECTED';
    shortReason?: string;
    distanceKm?: number | null;
    proximityLabel?: string | null;
}

interface RelevancyCandidatesTableProps {
    candidates: Candidate[];
    page: number;
    rowsPerPage: number;
    total: number;
    onPageChange: (event: unknown, newPage: number) => void;
    showAiMatch?: boolean;
    emptyMessage?: string;
}

const getAvatarFill = (rec: string): string =>
    rec === 'SHORTLIST'
        ? 'var(--acc)'
        : rec === 'MAYBE'
        ? 'var(--amber)'
        : 'var(--grey)';

const getInitials = (first: string, last: string): string =>
    ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || '?';

const recLabel: Record<string, { label: string; bg: string; color: string }> = {
    SHORTLIST:    { label: 'Shortlist', bg: 'var(--green-tint)', color: 'var(--green)' },
    MAYBE:        { label: 'Maybe',     bg: 'var(--amber-tint)', color: 'var(--amber)' },
    REJECT:       { label: 'Reject',    bg: 'var(--grey-tint)',  color: 'var(--grey)' },
    HARD_REJECTED:{ label: 'Filtered',  bg: 'var(--red-tint)',   color: 'var(--red)' },
};

const RelevancyCandidatesTable: React.FC<RelevancyCandidatesTableProps> = ({
    candidates,
    page,
    rowsPerPage,
    total,
    onPageChange,
    showAiMatch = true,
    emptyMessage = 'No relevant candidates found for this job.',
}) => {
    const isFirstPage = page === 0;
    const isLastPage = candidates.length < rowsPerPage;
    const start = total === 0 ? 0 : page * rowsPerPage + 1;
    const end = Math.min((page + 1) * rowsPerPage, total);

    if (isFirstPage && candidates.length === 0) {
        return (
            <Box className="rc-empty">
                <PeopleOutlineIcon className="rc-empty__icon" />
                <Typography variant="body2" color="textSecondary">
                    {emptyMessage}
                </Typography>
            </Box>
        );
    }

    if (!isFirstPage && candidates.length === 0) {
        return (
            <Box className="rc-empty">
                <Typography variant="body2" color="textSecondary">No more candidates on this page.</Typography>
                <IconButton size="small" onClick={(e) => onPageChange(e, page - 1)}>
                    <ChevronLeftIcon />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box>
            <Box className="rc-list">
                {candidates.map((candidate, idx) => {
                    const rank = page * rowsPerPage + idx + 1;
                    const score = typeof candidate.score === 'number' ? candidate.score : 0;
                    const scorePercent = Math.round(score);
                    const scoreColor = matchColor(score);
                    const avatarFill = showAiMatch
                        ? getAvatarFill(candidate.recommendation ?? 'REJECT')
                        : 'var(--acc)';
                    const initials = getInitials(candidate.firstName, candidate.lastName);
                    const rec = recLabel[candidate.recommendation ?? 'REJECT'] ?? recLabel['REJECT'];

                    return (
                        <Box
                            key={candidate.jobSeekerId}
                            className="rc-row"
                            // Only the two computed colours cross into the stylesheet; the rules
                            // that consume them live in the SCSS. See the file header.
                            style={{ '--rc-avatar-fill': avatarFill, '--rc-score-color': scoreColor } as React.CSSProperties}
                        >
                            {/* Rank */}
                            <Typography variant="caption" className="rc-row__rank">
                                {rank}
                            </Typography>

                            {/* Avatar */}
                            <Avatar className="rc-row__avatar">
                                {initials}
                            </Avatar>

                            {/* Name + role + reason */}
                            <Box className="rc-row__main">
                                <Box className="rc-row__titleline">
                                    <Typography variant="body2" noWrap className="rc-row__name">
                                        {candidate.firstName} {candidate.lastName}
                                    </Typography>
                                    {showAiMatch && (
                                        <Chip
                                            label={rec.label}
                                            size="small"
                                            className="rc-row__chip"
                                            sx={{ backgroundColor: rec.bg, color: rec.color }}
                                        />
                                    )}
                                    {!showAiMatch && typeof candidate.distanceKm === 'number' && (
                                        <Chip
                                            label={`${candidate.distanceKm.toFixed(1)} km`}
                                            size="small"
                                            className="rc-row__chip rc-row__chip--neutral"
                                        />
                                    )}
                                    {!showAiMatch && typeof candidate.distanceKm !== 'number' && candidate.proximityLabel && (
                                        <Chip
                                            label={candidate.proximityLabel}
                                            size="small"
                                            className="rc-row__chip rc-row__chip--neutral"
                                        />
                                    )}
                                </Box>
                                {candidate.currentRole && (
                                    <Typography variant="caption" noWrap className="rc-row__sub">
                                        {candidate.currentRole}
                                    </Typography>
                                )}
                                {showAiMatch && candidate.shortReason && (
                                    <Typography variant="caption" noWrap className="rc-row__sub">
                                        {candidate.shortReason}
                                    </Typography>
                                )}
                            </Box>

                            {/* Score + bar */}
                            {showAiMatch && (
                                <Box className="rc-row__score">
                                    <Typography variant="caption" className="rc-row__scorevalue">
                                        {scorePercent}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(scorePercent, 100)}
                                        className="rc-row__bar"
                                    />
                                </Box>
                            )}

                            {/* Open profile */}
                            <Tooltip title="Open candidate profile">
                                <IconButton
                                    size="small"
                                    className="rc-row__open"
                                    onClick={() => window.open(`/jobseekers/${candidate.jobSeekerId}`, '_blank')}
                                >
                                    <OpenInNewOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>

            {/* Pagination */}
            <Box className="rc-pager">
                <Tooltip title="Previous page">
                    <span>
                        <IconButton size="small" disabled={isFirstPage} onClick={(e) => onPageChange(e, page - 1)} className="rc-pager__btn">
                            <ChevronLeftIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography variant="caption" className="rc-pager__label">
                    {start}-{end} of {total}
                </Typography>
                <Tooltip title="Next page">
                    <span>
                        <IconButton size="small" disabled={isLastPage} onClick={(e) => onPageChange(e, page + 1)} className="rc-pager__btn">
                            <ChevronRightIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default RelevancyCandidatesTable;
